import { motion } from "framer-motion";
import {
  ShieldCheckIcon,
  CogIcon,
  ChartBarIcon,
  LockClosedIcon,
  ArrowPathIcon,
  ServerIcon,
  UserGroupIcon,
  CheckCircleIcon,
  GlobeAltIcon,
  CurrencyDollarIcon,
  CubeTransparentIcon,
} from "@heroicons/react/24/outline";
import { useSettings } from "../contexts/SettingsContext";
import { cn } from "../utils/cn";
import { EXTERNAL_LINKS } from "../constants/links";
import { useEffect } from "react";

const iconMap = {
  "Institutional-grade Custody": ShieldCheckIcon,
  "Regulatory Compliance Framework": LockClosedIcon,
  "High-frequency Execution Engine": ArrowPathIcon,
  "Custom Risk Management": ChartBarIcon,
  "API-first Integration": CogIcon,
  "Multi-entity Account Structure": UserGroupIcon,
  "On-chain Analytics": CheckCircleIcon,
  "Disaster Recovery Systems": ServerIcon,
};

export default function Institutions() {
  const { settings } = useSettings();
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  // Add structured data for SEO
  useEffect(() => {
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "What institutional features does de5.ai offer?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "de5.ai offers institutional-grade custody, regulatory compliance frameworks, high-frequency execution engines, custom risk management, API-first integration, multi-entity account structures, on-chain analytics, and disaster recovery systems for institutional clients and hedge funds.",
          },
        },
        {
          "@type": "Question",
          name: "How does de5.ai support automated rebalancing for institutions?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "de5.ai provides sophisticated rebalancing strategies with rule-based triggers, time-based scheduling with tax optimization, and multi-asset correlation analysis across digital assets, RWA, and traditional financial instruments.",
          },
        },
        {
          "@type": "Question",
          name: "What security features does de5.ai offer for institutional clients?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "de5.ai offers MPC wallet integration, regulatory compliance with AML/KYC tools, SOC 2 Type II certification, and bank-grade security infrastructure designed to meet the most stringent institutional requirements.",
          },
        },
      ],
    };

    // Create or update the structured data script tag
    let script = document.querySelector(
      "#institutions-structured-data"
    ) as HTMLScriptElement;
    if (!script) {
      script = document.createElement("script") as HTMLScriptElement;
      script.id = "institutions-structured-data";
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(structuredData);

    return () => {
      // Clean up the script tag when component unmounts
      if (script && script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  return (
    <section
      id="institutions"
      className="py-24 relative overflow-hidden"
      aria-labelledby="institutions-heading"
    >
      {/* Top fade from previous section */}
      <div
        className="pointer-events-none absolute inset-x-0 -top-16 h-16 bg-gradient-to-b from-transparent to-white/80 dark:to-[#0b0f19]/80 [mask-image:linear-gradient(to_bottom,black,transparent)]"
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="text-center mb-16">
          <motion.h2
            variants={itemVariants}
            id="institutions-heading"
            className="text-3xl sm:text-4xl lg:text-5xl font-oxanium font-bold mb-6"
            style={{ color: settings.primaryColor }}
          >
            {settings.institutionsTitle}
          </motion.h2>
          <motion.p
            variants={itemVariants}
            className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto"
          >
            {settings.institutionsSubtitle}
          </motion.p>
        </header>

        {/* Feature Cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
          role="list"
          aria-label="Institutional features"
        >
          {settings.institutionsFeatures.map((feature, index) => {
            const IconComponent =
              iconMap[feature.name as keyof typeof iconMap] || ShieldCheckIcon;
            return (
              <motion.article
                key={feature.name}
                variants={itemVariants}
                whileHover={{
                  scale: 1.05,
                  rotateY: 5,
                }}
                className="group relative"
                role="listitem"
                aria-describedby={`feature-description-${index}`}
              >
                <div
                  className={cn(
                    "h-full p-6 rounded-2xl glass-effect",
                    "hover:bg-white/20 dark:hover:bg-white/10",
                    "transition-all duration-300",
                    "border border-white/20 dark:border-white/10",
                    "hover:border-white/30 dark:hover:border-white/20"
                  )}
                >
                  {/* Icon with solid background */}
                  <motion.div
                    className="w-12 h-12 rounded-xl mb-4 flex items-center justify-center shadow-lg"
                    style={{ backgroundColor: "#e95c49" }}
                    whileHover={{ rotate: 5, scale: 1.1 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    aria-hidden="true"
                  >
                    <IconComponent className="h-6 w-6 text-white" />
                  </motion.div>

                  {/* Content */}
                  <h3 className="text-xl font-semibold mb-3 text-gray-900 dark:text-white font-oxanium">
                    {feature.name}
                  </h3>
                  <p
                    id={`feature-description-${index}`}
                    className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed"
                  >
                    {feature.description}
                  </p>

                  {/* Hover effect overlay */}
                  <motion.div
                    className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"
                    style={{ backgroundColor: "#e95c49" }}
                    aria-hidden="true"
                  />

                  {/* Glow effect */}
                  <motion.div
                    className="absolute -inset-0.5 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-sm"
                    style={{ backgroundColor: "#e95c49" }}
                    aria-hidden="true"
                  />
                </div>
              </motion.article>
            );
          })}
        </motion.div>

        {/* Detailed Feature Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Rebalancing Section */}
          <motion.article
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-white/10 dark:bg-white/5 backdrop-blur-md rounded-2xl p-8 border border-white/20 dark:border-white/10"
            aria-labelledby="rebalancing-heading"
          >
            <div className="flex items-center mb-6">
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center mr-4"
                style={{ backgroundColor: "#e95c49" }}
                aria-hidden="true"
              >
                <ChartBarIcon className="h-8 w-8 text-white" />
              </div>
              <h3
                id="rebalancing-heading"
                className="text-2xl font-bold text-gray-900 dark:text-white font-oxanium"
              >
                Advanced Portfolio Management
              </h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Sophisticated rebalancing strategies designed for institutional
              portfolios with complex requirements.
            </p>
            <ul className="space-y-4">
              <li className="flex items-start">
                <div
                  className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center mr-3 mt-0.5"
                  aria-hidden="true"
                >
                  <svg
                    className="w-3 h-3 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">
                    Rule-based Rebalancing
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Automated rebalancing based on market conditions, volatility
                    thresholds, and portfolio drift
                  </p>
                </div>
              </li>
              <li className="flex items-start">
                <div
                  className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center mr-3 mt-0.5"
                  aria-hidden="true"
                >
                  <svg
                    className="w-3 h-3 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">
                    Time-based Scheduling
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Configure periodic rebalancing at custom intervals with tax
                    optimization strategies
                  </p>
                </div>
              </li>
              <li className="flex items-start">
                <div
                  className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center mr-3 mt-0.5"
                  aria-hidden="true"
                >
                  <svg
                    className="w-3 h-3 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">
                    Multi-asset Correlation Analysis
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Advanced correlation modeling across digital assets, RWA,
                    and traditional financial instruments
                  </p>
                </div>
              </li>
            </ul>
          </motion.article>

          {/* Security Section */}
          <motion.article
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-white/10 dark:bg-white/5 backdrop-blur-md rounded-2xl p-8 border border-white/20 dark:border-white/10"
            aria-labelledby="security-heading"
          >
            <div className="flex items-center mb-6">
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center mr-4"
                style={{ backgroundColor: "#e95c49" }}
                aria-hidden="true"
              >
                <ShieldCheckIcon className="h-8 w-8 text-white" />
              </div>
              <h3
                id="security-heading"
                className="text-2xl font-bold text-gray-900 dark:text-white font-oxanium"
              >
                Enterprise Security & Compliance
              </h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Bank-grade security infrastructure designed to meet the most
              stringent institutional requirements.
            </p>
            <ul className="space-y-4">
              <li className="flex items-start">
                <div
                  className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center mr-3 mt-0.5"
                  aria-hidden="true"
                >
                  <svg
                    className="w-3 h-3 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">
                    MPC Wallet Integration
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Multi-party computation for enhanced security without single
                    points of failure
                  </p>
                </div>
              </li>
              <li className="flex items-start">
                <div
                  className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center mr-3 mt-0.5"
                  aria-hidden="true"
                >
                  <svg
                    className="w-3 h-3 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">
                    Regulatory Compliance
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Built-in AML/KYC tools and regulatory reporting across
                    multiple jurisdictions
                  </p>
                </div>
              </li>
              <li className="flex items-start">
                <div
                  className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center mr-3 mt-0.5"
                  aria-hidden="true"
                >
                  <svg
                    className="w-3 h-3 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">
                    SOC 2 Type II Certified
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Independent verification of security controls and
                    operational excellence
                  </p>
                </div>
              </li>
            </ul>
          </motion.article>
        </div>

        {/* Asset Support Section */}
        <motion.article
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="bg-white/10 dark:bg-white/5 backdrop-blur-md rounded-2xl p-8 border border-white/20 dark:border-white/10"
          aria-labelledby="assets-heading"
        >
          <div className="flex items-center mb-6">
            <div
              className="w-14 h-14 rounded-xl flex items-center justify-center mr-4"
              style={{ backgroundColor: "#e95c49" }}
              aria-hidden="true"
            >
              <GlobeAltIcon className="h-8 w-8 text-white" />
            </div>
            <h3
              id="assets-heading"
              className="text-2xl font-bold text-gray-900 dark:text-white font-oxanium"
            >
              Comprehensive Asset Universe
            </h3>
          </div>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Access a diverse range of digital and traditional assets through a
            single institutional-grade platform.
          </p>
          <div
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
            role="list"
            aria-label="Supported asset types"
          >
            <div
              className="text-center p-4 rounded-xl bg-white/5 dark:bg-white/5 border border-white/10 dark:border-white/10"
              role="listitem"
            >
              <div
                className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-3"
                aria-hidden="true"
              >
                <CurrencyDollarIcon className="h-6 w-6 text-blue-500" />
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                Digital Assets
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                1000+ cryptocurrencies with institutional liquidity and
                competitive pricing
              </p>
            </div>
            <div
              className="text-center p-4 rounded-xl bg-white/5 dark:bg-white/5 border border-white/10 dark:border-white/10"
              role="listitem"
            >
              <div
                className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-3"
                aria-hidden="true"
              >
                <GlobeAltIcon className="h-6 w-6 text-green-500" />
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                Tokenized RWA
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Real estate, commodities, and other physical assets with
                verified on-chain representation
              </p>
            </div>
            <div
              className="text-center p-4 rounded-xl bg-white/5 dark:bg-white/5 border border-white/10 dark:border-white/10"
              role="listitem"
            >
              <div
                className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-3"
                aria-hidden="true"
              >
                <CubeTransparentIcon className="h-6 w-6 text-purple-500" />
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                NFTs & Digital Collectibles
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Institutional-grade NFT portfolio management with automated
                valuation tracking
              </p>
            </div>
          </div>
        </motion.article>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <motion.button
            className="px-8 py-4 rounded-xl font-semibold text-lg text-white shadow-lg hover:shadow-xl transition-all duration-300"
            style={{
              backgroundColor: "#e95c49",
              boxShadow: "0 10px 25px rgba(233, 92, 73, 0.25)",
            }}
            onClick={() => {
              // Navigate to a contact page or open a contact modal
              const contactModal = document.querySelector(
                "[data-contact-modal]"
              ) as HTMLElement;
              if (contactModal) {
                contactModal.click();
              } else {
                // Navigate to documentation
                // window.location.href = EXTERNAL_LINKS.institutions;
                //open in a new window
                window.open(EXTERNAL_LINKS.institutions, "_blank");
              }
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#e95c49";
              e.currentTarget.style.boxShadow =
                "0 20px 40px rgba(233, 92, 73, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#e95c49";
              e.currentTarget.style.boxShadow =
                "0 10px 25px rgba(233, 92, 73, 0.25)";
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            aria-label={`Learn more about ${settings.institutionsTitle}`}
          >
            {settings.institutionsButtonText}
          </motion.button>
        </motion.div>
      </div>

      {/* Bottom fade to next section */}
      <div
        className="pointer-events-none absolute inset-x-0 -bottom-16 h-16 bg-gradient-to-t from-transparent to-white/80 dark:to-[#0b0f19]/80 [mask-image:linear-gradient(to_top,black,transparent)]"
        aria-hidden="true"
      />

      {/* Floating elements */}
      <motion.div
        className="absolute top-20 right-10 w-20 h-20 rounded-full blur-xl"
        style={{ backgroundColor: "rgba(233, 92, 73, 0.2)" }}
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.6, 0.3],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        aria-hidden="true"
      />

      <motion.div
        className="absolute bottom-20 left-10 w-32 h-32 rounded-full blur-xl"
        style={{ backgroundColor: "rgba(233, 92, 73, 0.15)" }}
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.2, 0.5, 0.2],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        aria-hidden="true"
      />
    </section>
  );
}
